import React, { useMemo } from 'react';
import { CourseIcon, COURSE_VARIANT_BY_ID } from './CourseOfferCard';
import { partitionDigitalCoursesForUser } from '../utils/personalAreaCourses';

function formatPurchaseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
}

function PersonalAreaMyCourseCard({ entry, index, onOpen }) {
  const { course, locked, orphan } = entry;
  const variant = COURSE_VARIANT_BY_ID[course.id] || 'default';
  const purchaseDate = formatPurchaseDate(entry.purchase?.purchaseDate);
  const canEnter = !locked && !orphan;

  return (
    <article
      className={`pa-my-course gl-offer-card--${variant}`}
      style={{ '--card-enter-delay': `${index * 80}ms` }}
    >
      <div className="pa-my-course__visual" aria-hidden="true">
        <div className="pa-my-course__glow" />
        <span className="pa-my-course__icon">
          <CourseIcon variant={variant} />
        </span>
      </div>
      <div className="pa-my-course__body">
        <div className="pa-my-course__meta">
          <span className="pa-my-course__badge">בבעלותכם</span>
          {purchaseDate ? <span className="pa-my-course__date">נרכש {purchaseDate}</span> : null}
        </div>
        <h3>{course.title}</h3>
        {course.summary ? <p className="pa-my-course__summary">{course.summary}</p> : null}
        {course.details?.duration ? (
          <p className="pa-my-course__duration">{course.details.duration} · {course.details.format}</p>
        ) : null}
        <div className="pa-my-course__progress" aria-label="התקדמות בקורס">
          <div className="pa-my-course__progress-track">
            <div className="pa-my-course__progress-fill" style={{ width: '12%' }} />
          </div>
          <span className="pa-my-course__progress-label">התחילו את המסלול — ההתקדמות תופיע כאן</span>
        </div>
        {canEnter ? (
          <button type="button" className="pa-my-course__cta" onClick={() => onOpen(course)}>
            המשיכו ללמוד
            <span aria-hidden="true">←</span>
          </button>
        ) : (
          <p className="pa-my-course__soon">הגישה לתוכן הקורס תיפתח בקרוב באפליקציה</p>
        )}
      </div>
    </article>
  );
}

function PersonalAreaPage({ user, onBack, onSignOut, onOpenCourse, onBrowseCatalog }) {
  const displayName = String(user?.name || '').trim() || 'אורח/ת';

  const { owned, discover } = useMemo(
    () => partitionDigitalCoursesForUser(user?.purchasedCourses),
    [user?.purchasedCourses],
  );

  const openCount = discover.filter((e) => !e.locked).length;
  const soonCount = discover.filter((e) => e.locked).length;

  return (
    <div className="personal-area-page">
      <div className="personal-area-page__orb personal-area-page__orb--gold" aria-hidden="true" />
      <div className="personal-area-page__orb personal-area-page__orb--blue" aria-hidden="true" />

      <header className="personal-area-page__nav">
        <button type="button" className="gl-brand personal-area-page__brand" onClick={onBack}>
          <img src="/phone/gl-logo.png" alt="" className="gl-brand-mark" />
          <span>Great Leaders</span>
        </button>
        <div className="personal-area-page__nav-actions">
          <button type="button" className="gl-courses-back" onClick={onBack}>
            חזרה לדף הבית
          </button>
          <button type="button" className="personal-area-page__signout" onClick={onSignOut}>
            יציאה
          </button>
        </div>
      </header>

      <main className="personal-area-page__main">
        <section className="pa-hero" aria-label="ברוכים הבאים">
          <div className="pa-hero__profile">
            <div className="pa-hero__copy">
              <p className="gl-kicker pa-hero__kicker">איזור אישי</p>
              <h1>
                שלום,
                <span className="pa-hero__name">{displayName}</span>
              </h1>
              <p className="pa-hero__lead">
                המרחב שלכם ללמידה — הקורסים שרכשתם והמשך המסע שלכם.
              </p>
              {onBrowseCatalog ? (
                <button
                  type="button"
                  className="gl-academy-hero-cta gl-academy-hero-cta--primary pa-hero__catalog"
                  onClick={onBrowseCatalog}
                >
                  קטלוג קורסים
                  <span className="pa-hero__catalog-arrow" aria-hidden="true">←</span>
                </button>
              ) : null}
            </div>
          </div>
        </section>

        <div className="pa-stats-bar" role="list" aria-label="סיכום">
          <div className="pa-stats-bar__cell" role="listitem">
            <strong>{owned.length}</strong>
            <span>קורסים שלי</span>
          </div>
          <div className="pa-stats-bar__cell" role="listitem">
            <strong>{openCount}</strong>
            <span>זמינים לרכישה</span>
          </div>
          <div className="pa-stats-bar__cell" role="listitem">
            <strong>{soonCount}</strong>
            <span>בקרוב בקטלוג</span>
          </div>
        </div>

        <section className="pa-section" aria-labelledby="pa-my-courses-title">
          <div className="pa-section__head">
            <p className="gl-kicker pa-section__kicker">הלמידה שלכם</p>
            <h2 id="pa-my-courses-title">הקורסים שלי</h2>
            <p className="pa-section__lead">המשיכו מאיפה שהפסקתם או התחילו מסלול חדש שכבר שייך לכם.</p>
          </div>
          {owned.length === 0 ? (
            <div className="pa-empty">
              <span className="pa-empty__icon" aria-hidden="true">📚</span>
              <h3 className="pa-empty__title">המדף שלכם מחכה לקורס הראשון</h3>
              <p className="pa-empty__text">
                בחרו מסלול מהקטלוג — אחרי הרכישה הוא יופיע כאן מיד, מוכן להמשך למידה.
              </p>
              {onBrowseCatalog ? (
                <button
                  type="button"
                  className="gl-academy-hero-cta gl-academy-hero-cta--primary pa-empty__cta"
                  onClick={onBrowseCatalog}
                >
                  לקטלוג הקורסים
                  <span aria-hidden="true">←</span>
                </button>
              ) : null}
            </div>
          ) : (
            <div className="pa-my-grid">
              {owned.map((entry, index) => (
                <PersonalAreaMyCourseCard
                  key={entry.course.id}
                  entry={entry}
                  index={index}
                  onOpen={onOpenCourse}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default PersonalAreaPage;
